import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Paper, Typography } from "@mui/material";
import { TreeView } from "@mui/x-tree-view";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getRequestWithToken } from "../../utils/ApiRequests";
import ObjectIdBadge from "../JobSectOccuRole/components/ObjectIdBadge";

const apiBase = "../v3/admin";

export const getCandidateTreeNodeIds = (familyId = "", groupId = "", roleId = "") => ({
  family: familyId ? `candidate-family:${familyId}` : "",
  group: familyId && groupId ? `candidate-group:${familyId}:${groupId}` : "",
  role: familyId && groupId && roleId ? `candidate-role:${familyId}:${groupId}:${roleId}` : "",
});

export const groupNcoLinksBySector = (links = [], sectors = []) => {
  const sectorNames = new Map(sectors.map((sector) => [String(sector._id), sector.sectorNameEn]));
  const sectorMap = new Map();
  links.forEach((link) => {
    const ncoRole = link.ncoJobRole;
    const occupation = ncoRole?.occupation;
    if (!ncoRole?._id || !occupation?._id) return;
    (occupation.sectorIds || []).forEach((sectorValue) => {
      const sectorId = String(sectorValue?._id || sectorValue);
      if (!sectorId) return;
      if (!sectorMap.has(sectorId)) sectorMap.set(sectorId, { _id: sectorId, name: sectorNames.get(sectorId) || sectorId, occupations: new Map() });
      const sector = sectorMap.get(sectorId);
      const occupationId = String(occupation._id);
      if (!sector.occupations.has(occupationId)) sector.occupations.set(occupationId, { _id: occupationId, name: occupation.name || occupationId, roles: new Map() });
      sector.occupations.get(occupationId).roles.set(String(ncoRole._id), { ...ncoRole, linkId: link._id });
    });
  });
  return [...sectorMap.values()].map((sector) => ({
    ...sector,
    occupations: [...sector.occupations.values()].map((occupation) => ({ ...occupation, roles: [...occupation.roles.values()] })),
  }));
};

const styles = {
  family: { color: "#7C3AED", background: "rgba(124,58,237,0.12)", Icon: GroupsOutlinedIcon, size: 34, font: 14, weight: 700 },
  group: { color: "#D66A00", background: "rgba(232,111,0,0.13)", Icon: AccountTreeOutlinedIcon, size: 31, font: 13.5, weight: 650 },
  candidateRole: { color: "#147D70", background: "rgba(20,125,112,0.13)", Icon: BadgeOutlinedIcon, size: 29, font: 13, weight: 650 },
  sector: { color: "#5B5BD6", background: "rgba(91,91,214,0.13)", Icon: CategoryOutlinedIcon, size: 27, font: 12.5, weight: 600 },
  occupation: { color: "#E86F00", background: "rgba(232,111,0,0.13)", Icon: AccountTreeOutlinedIcon, size: 25, font: 12, weight: 550 },
  ncoRole: { color: "#25766C", background: "rgba(37,118,108,0.13)", Icon: WorkOutlineIcon, size: 23, font: 12, weight: 500 },
};

const NodeLabel = ({ level, name, objectId = "", businessId = "", icon = "", aliases = [], ncoCode = "", count, onCopy, onActivate }) => {
  const style = styles[level];
  const Icon = style.Icon;
  return (
    <Box display="flex" alignItems="center" gap={1} py={0.4} minWidth={0} width="100%">
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: style.size, height: style.size, flexShrink: 0, borderRadius: 1.5, color: style.color, bgcolor: style.background, fontSize: 17 }}>
        {level === "family" && icon ? icon : <Icon sx={{ fontSize: style.size - 14 }} />}
      </Box>
      <Box minWidth={0} flex={1} onClick={onActivate ? (event) => { event.preventDefault(); event.stopPropagation(); onActivate(); } : undefined} sx={{ cursor: onActivate ? "pointer" : "default" }}>
        <Box display="flex" alignItems="center" gap={0.65} flexWrap="wrap">
          <Typography noWrap sx={{ fontSize: style.font, fontWeight: style.weight, lineHeight: 1.25 }}>{name}</Typography>
          {count !== undefined && <Chip size="small" label={`${count} link${count === 1 ? "" : "s"}`} sx={{ height: 18, fontSize: 9, color: "#147D70", bgcolor: "rgba(20,125,112,0.11)" }} />}
          {ncoCode && <Chip size="small" label={`NCO ${ncoCode}`} sx={{ height: 18, fontSize: 9, color: "#C66100", bgcolor: "rgba(255,140,0,0.13)" }} />}
        </Box>
        {businessId && <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 9.5 }}>Business ID: {businessId}</Typography>}
        {aliases.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 9.5 }} noWrap>Aliases: {aliases.slice(0, 3).join(", ")}</Typography>}
        <ObjectIdBadge objectId={objectId} onCopy={onCopy} compact label={level === "ncoRole" ? "NCO Role ID" : "ObjectId"} />
      </Box>
    </Box>
  );
};

const StatusLabel = ({ message, loading = false, error = false, onRetry }) => (
  <Box display="flex" alignItems="center" gap={0.8} py={0.7} color={error ? "error.main" : "text.secondary"}>
    {loading ? <CircularProgress size={14} /> : error ? <ErrorOutlineIcon sx={{ fontSize: 16 }} /> : <InfoOutlinedIcon sx={{ fontSize: 16 }} />}
    <Typography variant="caption">{message}</Typography>
    {onRetry && <Button size="small" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onRetry(); }} sx={{ minWidth: 0, px: 0.75, fontSize: 10 }}>Retry</Button>}
  </Box>
);

const itemSx = (level, selected = false) => ({
  "& > .MuiTreeItem-content": {
    minHeight: level === "family" ? 46 : level === "group" ? 43 : 39,
    mb: 0.3,
    pr: 1.2,
    borderRadius: 2,
    borderLeft: level === "candidateRole" && selected ? "3px solid #147D70" : "3px solid transparent",
    bgcolor: selected ? (theme) => theme.palette.mode === "dark" ? "rgba(20,125,112,0.2)" : "rgba(20,125,112,0.13)" : "transparent",
    "&:hover": { bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(92,75,58,0.07)" },
    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: (theme) => `${selected ? theme.palette.mode === "dark" ? "rgba(20,125,112,0.2)" : "rgba(20,125,112,0.13)" : "transparent"} !important` },
  },
});

const CandidateRoleHierarchyTree = ({ sectors = [], taxonomyRevision = 0, linkRevision = 0, selectedFamilyId = "", selectedGroupId = "", selectedRoleId = "", onRoleActivate = () => {}, onCopy = () => {} }) => {
  const [families, setFamilies] = useState([]);
  const [groupsByFamily, setGroupsByFamily] = useState({});
  const [rolesByGroup, setRolesByGroup] = useState({});
  const [linksByRole, setLinksByRole] = useState({});
  const [expanded, setExpanded] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState({});
  const [errors, setErrors] = useState({});

  const setLoading = (nodeId, value) => setLoadingNodes((previous) => ({ ...previous, [nodeId]: value }));

  const loadFamilies = useCallback(async () => {
    const nodeId = "candidate-root";
    setLoadingNodes((previous) => ({ ...previous, [nodeId]: true }));
    setErrors((previous) => ({ ...previous, [nodeId]: "" }));
    try {
      const response = await getRequestWithToken(`${apiBase}/families`);
      setFamilies(response.data?.data || []);
    } catch (error) {
      setErrors((previous) => ({ ...previous, [nodeId]: error?.response?.data?.msg || "Could not load Candidate Families." }));
    } finally {
      setLoadingNodes((previous) => ({ ...previous, [nodeId]: false }));
    }
  }, []);

  useEffect(() => { loadFamilies(); }, [loadFamilies, taxonomyRevision]);

  useEffect(() => {
    setGroupsByFamily({});
    setRolesByGroup({});
    setLinksByRole({});
    setExpanded([]);
    setErrors({});
  }, [taxonomyRevision]);

  useEffect(() => {
    setLinksByRole({});
    setExpanded((previous) => previous.filter((nodeId) => !nodeId.startsWith("candidate-role:")));
    setErrors((previous) => Object.fromEntries(Object.entries(previous).filter(([nodeId]) => !nodeId.startsWith("candidate-role:"))));
  }, [linkRevision]);

  const loadGroups = async (familyId, nodeId) => {
    if (Object.prototype.hasOwnProperty.call(groupsByFamily, familyId) || loadingNodes[nodeId]) return;
    setLoading(nodeId, true);
    setErrors((previous) => ({ ...previous, [nodeId]: "" }));
    try {
      const response = await getRequestWithToken(`${apiBase}/families/${familyId}/groups`);
      setGroupsByFamily((previous) => ({ ...previous, [familyId]: response.data?.data || [] }));
    } catch (error) {
      setErrors((previous) => ({ ...previous, [nodeId]: error?.response?.data?.msg || "Could not load Role Groups." }));
    } finally {
      setLoading(nodeId, false);
    }
  };

  const loadRoles = async (groupId, nodeId) => {
    if (Object.prototype.hasOwnProperty.call(rolesByGroup, groupId) || loadingNodes[nodeId]) return;
    setLoading(nodeId, true);
    setErrors((previous) => ({ ...previous, [nodeId]: "" }));
    try {
      const response = await getRequestWithToken(`${apiBase}/groups/${groupId}/roles`);
      setRolesByGroup((previous) => ({ ...previous, [groupId]: response.data?.data || [] }));
    } catch (error) {
      setErrors((previous) => ({ ...previous, [nodeId]: error?.response?.data?.msg || "Could not load Candidate Roles." }));
    } finally {
      setLoading(nodeId, false);
    }
  };

  const loadLinks = async (roleId, nodeId) => {
    if (Object.prototype.hasOwnProperty.call(linksByRole, roleId) || loadingNodes[nodeId]) return;
    setLoading(nodeId, true);
    setErrors((previous) => ({ ...previous, [nodeId]: "" }));
    try {
      const links = [];
      let page = 1;
      let totalPages = 1;
      do {
        const query = new URLSearchParams({ mapped: "true", candidate_role_id: roleId, limit: "100", page: String(page) }).toString();
        const response = await getRequestWithToken(`${apiBase}/candidate-role-mappings/nco-links?${query}`);
        if (response.data?.status !== "Success") throw new Error(response.data?.msg || "Could not load linked NCO roles.");
        links.push(...(response.data?.data?.links || []));
        totalPages = response.data?.data?.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);
      const unique = links.filter((link, index, all) => all.findIndex((item) => item._id === link._id) === index);
      setLinksByRole((previous) => ({ ...previous, [roleId]: unique }));
    } catch (error) {
      setErrors((previous) => ({ ...previous, [nodeId]: error?.response?.data?.msg || error?.message || "Could not load linked NCO roles." }));
    } finally {
      setLoading(nodeId, false);
    }
  };

  const handleToggle = (_, nodeIds) => {
    const newlyExpanded = nodeIds.filter((nodeId) => !expanded.includes(nodeId));
    setExpanded(nodeIds);
    newlyExpanded.forEach((nodeId) => {
      const parts = nodeId.split(":");
      if (parts[0] === "candidate-family" && parts.length === 2) loadGroups(parts[1], nodeId);
      if (parts[0] === "candidate-group" && parts.length === 3) loadRoles(parts[2], nodeId);
      if (parts[0] === "candidate-role" && parts.length === 4) loadLinks(parts[3], nodeId);
    });
  };

  const selectedNodes = getCandidateTreeNodeIds(selectedFamilyId, selectedGroupId, selectedRoleId);
  const selectedNodeId = selectedNodes.role || selectedNodes.group || selectedNodes.family;
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, minHeight: 420, borderRadius: 2.5, bgcolor: (theme) => theme.palette.mode === "dark" ? "#1b1b1b" : "#f1ede8", borderColor: (theme) => theme.palette.mode === "dark" ? "#303030" : "#d4cdc4", boxShadow: (theme) => theme.palette.mode === "dark" ? "inset 0 1px 0 rgba(255,255,255,0.03)" : "inset 0 1px 0 rgba(255,255,255,0.7), 0 8px 22px rgba(50,38,26,0.07)" }}>
      <Box mb={1.5}>
        <Typography variant="subtitle1" fontWeight={750}>Candidate Family → Group → Role → Linked NCO hierarchy</Typography>
        <Typography variant="caption" color="text.secondary">Expand a Candidate Role to inspect every linked Sector, Occupation, and NCO Role.</Typography>
      </Box>
      {loadingNodes["candidate-root"] && families.length === 0 ? <StatusLabel message="Loading Candidate Families..." loading /> : errors["candidate-root"] && families.length === 0 ? <StatusLabel message={errors["candidate-root"]} error onRetry={loadFamilies} /> : families.length === 0 ? <StatusLabel message="No active Candidate Families" /> : (
        <TreeView
          expanded={expanded}
          selected={selectedNodeId}
          onNodeToggle={handleToggle}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          sx={{ flexGrow: 1, overflowY: "auto", maxHeight: "65vh", pr: 0.75, "& .MuiTreeItem-iconContainer": { display: "flex !important", width: "22px !important", color: "text.secondary" }, "& .MuiTreeItem-group": { ml: 2.3, pl: 2.3, borderLeft: "1px dashed", borderColor: (theme) => theme.palette.mode === "dark" ? "#45413d" : "#c9bfb4" }, "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(110,95,80,0.35)", borderRadius: 5 } }}
        >
          {families.map((family) => {
            const familyNodeId = `candidate-family:${family._id}`;
            const groups = groupsByFamily[family._id];
            return (
              <TreeItem key={familyNodeId} nodeId={familyNodeId} label={<NodeLabel level="family" name={family.family_name || family.family_id} objectId={family._id} businessId={family.family_id} icon={family.family_icon} onCopy={onCopy} />} sx={itemSx("family", selectedFamilyId === family._id)}>
                {groups === undefined && !loadingNodes[familyNodeId] && !errors[familyNodeId] && <TreeItem nodeId={`${familyNodeId}:placeholder`} label={<StatusLabel message="Expand to load Role Groups" />} />}
                {loadingNodes[familyNodeId] && <TreeItem nodeId={`${familyNodeId}:loading`} label={<StatusLabel message="Loading Role Groups..." loading />} />}
                {errors[familyNodeId] && <TreeItem nodeId={`${familyNodeId}:error`} label={<StatusLabel message={errors[familyNodeId]} error onRetry={() => loadGroups(family._id, familyNodeId)} />} />}
                {groups?.length === 0 && <TreeItem nodeId={`${familyNodeId}:empty`} label={<StatusLabel message="No active Role Groups" />} />}
                {(groups || []).map((group) => {
                  const groupNodeId = `candidate-group:${family._id}:${group._id}`;
                  const roles = rolesByGroup[group._id];
                  return (
                    <TreeItem key={groupNodeId} nodeId={groupNodeId} label={<NodeLabel level="group" name={group.group_name || group.group_id} objectId={group._id} businessId={group.group_id} onCopy={onCopy} />} sx={itemSx("group", selectedFamilyId === family._id && selectedGroupId === group._id)}>
                      {roles === undefined && !loadingNodes[groupNodeId] && !errors[groupNodeId] && <TreeItem nodeId={`${groupNodeId}:placeholder`} label={<StatusLabel message="Expand to load Candidate Roles" />} />}
                      {loadingNodes[groupNodeId] && <TreeItem nodeId={`${groupNodeId}:loading`} label={<StatusLabel message="Loading Candidate Roles..." loading />} />}
                      {errors[groupNodeId] && <TreeItem nodeId={`${groupNodeId}:error`} label={<StatusLabel message={errors[groupNodeId]} error onRetry={() => loadRoles(group._id, groupNodeId)} />} />}
                      {roles?.length === 0 && <TreeItem nodeId={`${groupNodeId}:empty`} label={<StatusLabel message="No active Candidate Roles" />} />}
                      {(roles || []).map((role) => {
                        const roleNodeId = `candidate-role:${family._id}:${group._id}:${role._id}`;
                        const links = linksByRole[role._id];
                        const paths = links === undefined ? [] : groupNcoLinksBySector(links, sectors);
                        return (
                          <TreeItem key={roleNodeId} nodeId={roleNodeId} label={<NodeLabel level="candidateRole" name={role.role_ui_label || role.role_name || role.role_id} objectId={role._id} businessId={role.role_id} aliases={role.aliases?.en || []} count={links?.length} onCopy={onCopy} onActivate={() => onRoleActivate({ ...role, group_ref: { ...group, family_ref: family } })} />} sx={itemSx("candidateRole", selectedRoleId === role._id)}>
                            {links === undefined && !loadingNodes[roleNodeId] && !errors[roleNodeId] && <TreeItem nodeId={`${roleNodeId}:placeholder`} label={<StatusLabel message="Expand to load linked NCO roles" />} />}
                            {loadingNodes[roleNodeId] && <TreeItem nodeId={`${roleNodeId}:loading`} label={<StatusLabel message="Loading linked NCO roles..." loading />} />}
                            {errors[roleNodeId] && <TreeItem nodeId={`${roleNodeId}:error`} label={<StatusLabel message={errors[roleNodeId]} error onRetry={() => loadLinks(role._id, roleNodeId)} />} />}
                            {links?.length === 0 && <TreeItem nodeId={`${roleNodeId}:empty`} label={<StatusLabel message="No linked NCO roles" />} />}
                            {paths.map((sector) => (
                              <TreeItem key={`${roleNodeId}:sector:${sector._id}`} nodeId={`${roleNodeId}:sector:${sector._id}`} label={<NodeLabel level="sector" name={sector.name} objectId={sector._id} onCopy={onCopy} />} sx={itemSx("sector")}>
                                {sector.occupations.map((occupation) => (
                                  <TreeItem key={`${roleNodeId}:occupation:${sector._id}:${occupation._id}`} nodeId={`${roleNodeId}:occupation:${sector._id}:${occupation._id}`} label={<NodeLabel level="occupation" name={occupation.name} objectId={occupation._id} onCopy={onCopy} />} sx={itemSx("occupation")}>
                                    {occupation.roles.map((ncoRole) => (
                                      <TreeItem key={`${roleNodeId}:nco-role:${sector._id}:${occupation._id}:${ncoRole._id}`} nodeId={`${roleNodeId}:nco-role:${sector._id}:${occupation._id}:${ncoRole._id}`} label={<Box><NodeLabel level="ncoRole" name={ncoRole.name || ncoRole._id} objectId={ncoRole._id} ncoCode={ncoRole.ncoCode} onCopy={onCopy} /><ObjectIdBadge objectId={ncoRole.linkId} label="Link ID" onCopy={onCopy} compact /></Box>} sx={itemSx("ncoRole")} />
                                    ))}
                                  </TreeItem>
                                ))}
                              </TreeItem>
                            ))}
                          </TreeItem>
                        );
                      })}
                    </TreeItem>
                  );
                })}
              </TreeItem>
            );
          })}
        </TreeView>
      )}
    </Paper>
  );
};

export default CandidateRoleHierarchyTree;
